import { randomExportSizes } from '../data/content/randomExport';

export { randomExportSizes };

/*
 * Uniform random sample without replacement (partial Fisher-Yates).
 *
 * Never mutates the pool, and returns the whole pool when `size` exceeds it so
 * callers can always render what they get back and report the real count.
 */
export const randomSample = (pool, size) => {
  if (!Array.isArray(pool) || pool.length === 0 || size <= 0) {
    return [];
  }

  const items = [...pool];
  const take = Math.min(size, items.length);

  for (let i = 0; i < take; i += 1) {
    const j = i + Math.floor(Math.random() * (items.length - i));
    const swap = items[i];
    items[i] = items[j];
    items[j] = swap;
  }

  return items.slice(0, take);
};

/*
 * How many questions a piece of content contributes.
 *
 * The search index stamps `_questionCount` on every record (questions.length for
 * a testlet, 1 for a stand-alone question), but the search page strips it while
 * building cards, so fall back to the document itself.
 */
export const questionsInContent = content => {
  if (!content) {
    return 0;
  }

  if (typeof content._questionCount === 'number' && content._questionCount > 0) {
    return content._questionCount;
  }

  if (Array.isArray(content.questions)) {
    return content.questions.length;
  }

  return 1;
};

/*
 * Draw content at random so that the questions add up to `targetQuestions`.
 *
 * A testlet is a patient case plus the questions written against it, so it can
 * only be taken whole, and taking content in the order it comes up can stop a
 * question or two short. The faculty would then have to find the rest by hand,
 * so a draw never comes back short while the pool can fill it:
 *
 *   1. a set that adds up exactly, whenever any combination in the pool does;
 *   2. failing that, the set that goes over by the least;
 *   3. the whole pool, when it holds no more than was asked for.
 *
 * The pool is shuffled first, so which of the possible sets comes out is left to
 * chance and a re-roll gives another. `isOnlySet` says when there was only one
 * to give, so the UI can explain why a re-roll keeps returning it.
 */
export const buildRandomSet = (pool, targetQuestions) => {
  const empty = { items: [], isOnlySet: false, questionCount: 0, testletCount: 0, standAloneCount: 0 };

  if (!Array.isArray(pool) || pool.length === 0 || !targetQuestions || targetQuestions <= 0) {
    return empty;
  }

  const candidates = randomSample(pool, pool.length).filter(item => questionsInContent(item) > 0);
  const sizes = candidates.map(questionsInContent);
  const available = sizes.reduce((total, size) => total + size, 0);

  if (available === 0) {
    return empty;
  }

  let items = candidates;
  let isOnlySet = true;

  if (available > targetQuestions) {
    // the smallest total past the target is always within one content of it
    const limit = targetQuestions + Math.max(...sizes) - 1;
    const sets = new Array(limit + 1).fill(0); // how many sets add up to each total, counted no further than two
    const lastAdded = new Array(limit + 1).fill(-1); // the content that first completed each total
    sets[0] = 1;

    sizes.forEach((size, index) => {
      for (let total = limit; total >= size; total -= 1) {
        if (sets[total - size] > 0) {
          if (sets[total] === 0) {
            lastAdded[total] = index;
          }

          sets[total] = Math.min(2, sets[total] + sets[total - size]);
        }
      }
    });

    let total = targetQuestions;
    while (sets[total] === 0) {
      total += 1;
    }

    isOnlySet = sets[total] === 1;
    items = [];

    while (total > 0) {
      items.push(candidates[lastAdded[total]]);
      total -= sizes[lastAdded[total]];
    }
  }

  const questionCount = items.reduce((total, item) => total + questionsInContent(item), 0);
  const testletCount = items.filter(item => questionsInContent(item) > 1 || item.content_type === 'testlet').length;

  return {
    items,
    isOnlySet,
    questionCount,
    testletCount,
    standAloneCount: items.length - testletCount
  };
};
